import { Text, VSpacer } from '@/components/DesignSystem';
import {
  CustomerAutocomplete,
  CustomerOption,
  toCustomerOption,
} from '@/components/shared/CustomerAutocomplete';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { PointsAuditApi } from '@/utilities/api/PointsAuditApi';
import { AuditEventDetailDialog } from './AuditEventDetailDialog';
import { describeAuditEvent } from './describeAuditEvent';
import { describeChanges } from './describeChanges';
import { eventTypeLabel } from './eventTypeLabel';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { ApiPointsAuditLog } from '@api/interfaces';
import { PointsAuditActorType, PointsAuditEventType } from '@shared/enums';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';

const NO_WRAP = { whiteSpace: 'nowrap' } as const;
const PAGE_LIMIT = 10;

/** Who changed a farmer's or retailer's points, when, why and from which CSV. */
export const PointsAuditTrail = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const retailerId = searchParams.get('retailerId') ?? '';
  const [eventType, setEventType] = useState<PointsAuditEventType | ''>('');
  const farmerIds = searchParams.getAll('farmerId');
  const [selectedEvent, setSelectedEvent] = useState<ApiPointsAuditLog>();
  // The URL holds only ids, so keep the names of customers picked here for their chips.
  const [pickedCustomers, setPickedCustomers] = useState<CustomerOption[]>([]);
  const [page, setPage] = useState(0);

  const { data: retailers } = useQuery(
    [QueryKeys.GET_RETAILERS],
    () => RetailerApi.list({ limit: 200 }),
  );
  const retailerNameById = new Map(
    (retailers?.data ?? []).map((retailer) => [retailer.id, retailer.name]),
  );
  // With no retailer selected the trail lists every retailer's rows, so each row names its own.
  const isAllRetailers = !retailerId;
  const columnCount = isAllRetailers ? 7 : 6;

  // A customer chosen from a link may not be on a page loaded yet, so name the chosen ones directly.
  const { data: chosenCustomers, isFetching: areChosenCustomersFetching } = useQuery(
    [QueryKeys.LIST_USERS, 'points-audit-chosen-customers', farmerIds],
    () => UserApi.list({ id: farmerIds, limit: farmerIds.length }),
    { enabled: !!farmerIds.length, keepPreviousData: true },
  );
  const knownCustomers = new Map([
    ...pickedCustomers,
    ...(chosenCustomers?.data ?? []).map(toCustomerOption),
  ].map((customer) => [customer.id, customer]));
  // An id the lookup has not named, or could not find, is the only label left to show.
  const selectedCustomers = farmerIds.map((id) => knownCustomers.get(id) ?? {
    id,
    label: areChosenCustomersFetching ? t($ => $.customerAutocomplete.loading) : id,
  });

  const filter = {
    ...(eventType ? { eventType } : {}),
    ...(farmerIds.length ? { farmerIds } : {}),
    ...(retailerId ? { retailerId } : {}),
    limit: PAGE_LIMIT,
    page,
  };

  const { data: summary } = useQuery(
    [QueryKeys.GET_POINTS_AUDIT_SUMMARY, retailerId],
    () => PointsAuditApi.summary(retailerId),
    { enabled: !!retailerId },
  );

  const { data: events, isFetching } = useQuery(
    [QueryKeys.GET_POINTS_AUDIT_LOG, retailerId, farmerIds, eventType, page],
    () => PointsAuditApi.list(filter),
    { keepPreviousData: true },
  );

  const onRetailerChange = (id: string) => {
    setPage(0);
    // A farmer from the previous retailer would match nothing.
    setPickedCustomers([]);
    setSearchParams(id ? { retailerId: id } : {});
  };

  const onCustomersChange = (chosen: CustomerOption[]) => {
    setPage(0);
    setPickedCustomers(chosen);
    const next = new URLSearchParams(retailerId ? { retailerId } : {});
    chosen.forEach(({ id }) => next.append('farmerId', id));
    setSearchParams(next);
  };

  // Crons and integrations have no user, so name the kind of actor instead.
  const actorLabel = (row: ApiPointsAuditLog) => {
    if (row.actorName) { return row.actorName; }
    if (row.actorType === PointsAuditActorType.Cron) {
      return t($ => $.pointsAudit.actors.system);
    }
    if (row.actorType === PointsAuditActorType.Integration) {
      return t($ => $.pointsAudit.actors.integration);
    }
    return t($ => $.pointsAudit.actors.unknown);
  };

  const renderDiff = (row: ApiPointsAuditLog) => {
    const changes = describeChanges(row.before, row.after, t);
    if (!changes.length) { return null; }

    return (
      <Stack gap="2px">
        {changes.map(({ key, text }) => (
          <Text category="body-small" key={key}>{text}</Text>
        ))}
      </Stack>
    );
  };

  const tile = (label: string, value: number | undefined) => (
    <Grid item xs={3}>
      <Card sx={{ p: 2 }}>
        <Text category="label-medium">{label}</Text>
        <VSpacer size="2" />
        {value === undefined
          ? <Skeleton height="30px" variant="text" width="60%" />
          : <Text category="title-medium">{value.toLocaleString()}</Text>}
      </Card>
    </Grid>
  );

  return (
    <Container maxWidth="xl">
      <VSpacer size="7" />
      <Text category="headline-medium">{t($ => $.pointsAudit.title)}</Text>
      <VSpacer size="3" />
      <Text category="body-medium">{t($ => $.pointsAudit.description)}</Text>
      <VSpacer size="6" />

      <Stack direction="row" gap="16px">
        <TextField
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          data-testid="points-audit-retailer-select"
          label={t($ => $.pointsAudit.filters.retailer)}
          onChange={(event) => onRetailerChange(event.target.value)}
          select
          sx={{ minWidth: 280 }}
          value={retailerId}
        >
          <MenuItem value="">{t($ => $.pointsAudit.filters.allRetailers)}</MenuItem>
          {retailers?.data.map((retailer) => (
            <MenuItem key={retailer.id} value={retailer.id}>{retailer.name}</MenuItem>
          ))}
        </TextField>

        <CustomerAutocomplete
          label={t($ => $.common.customer)}
          limitTags={1}
          multiple
          onChange={onCustomersChange}
          retailerId={retailerId}
          testID="points-audit-customer-select"
          value={selectedCustomers}
        />

        <TextField
          data-testid="points-audit-event-type-select"
          label={t($ => $.pointsAudit.filters.eventType)}
          onChange={(event) => {
            setPage(0);
            setEventType(event.target.value as PointsAuditEventType | '');
          }}
          select
          sx={{ minWidth: 240 }}
          value={eventType}
        >
          <MenuItem value="">{t($ => $.pointsAudit.filters.allEvents)}</MenuItem>
          {Object.values(PointsAuditEventType)
            .map((type) => ({ label: eventTypeLabel(type, t), type }))
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(({ label, type }) => (
              <MenuItem key={type} value={type}>{label}</MenuItem>
            ))}
        </TextField>

        <Box flexGrow={1} />
        <Link to={`${Routes.ADMIN_POINTS_AUDIT_PROCESSES}?retailerId=${retailerId}`}>
          <Text category="body-medium">{t($ => $.pointsAudit.viewProcesses)}</Text>
        </Link>
      </Stack>

      <VSpacer size="6" />

      {!!retailerId && (
        <Grid container spacing={2}>
            {tile(t($ => $.pointsAudit.tiles.totalEvents), summary?.total)}
            {tile(t($ => $.pointsAudit.tiles.processes), summary?.processCount)}
            {tile(
              t($ => $.pointsAudit.tiles.byPeople),
              summary && (summary.byActorType[PointsAuditActorType.User] ?? 0),
            )}
            {tile(
              t($ => $.pointsAudit.tiles.byImports),
              summary && (summary.byActorType[PointsAuditActorType.Import] ?? 0),
            )}
        </Grid>
      )}

      <VSpacer size="6" />

          {!retailerId && (
            <>
              <Text category="body-medium" testID="points-audit-all-retailers-notice">
                {t($ => $.pointsAudit.allRetailersNotice)}
              </Text>
              <VSpacer size="3" />
            </>
          )}
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={160}>{t($ => $.pointsAudit.columns.when)}</TableCell>
                  {isAllRetailers && (
                    <TableCell width={180}>{t($ => $.pointsAudit.columns.retailer)}</TableCell>
                  )}
                  <TableCell width={150}>{t($ => $.pointsAudit.columns.who)}</TableCell>
                  <TableCell width={180}>{t($ => $.pointsAudit.columns.event)}</TableCell>
                  <TableCell>{t($ => $.pointsAudit.columns.whatChanged)}</TableCell>
                  <TableCell width={220}>{t($ => $.pointsAudit.columns.why)}</TableCell>
                  <TableCell width={200}>{t($ => $.pointsAudit.columns.source)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching && !events && (
                  <TableRow>
                    <TableCell align="center" colSpan={columnCount}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {events?.data.length === 0 && (
                  <TableRow>
                    <TableCell align="center" colSpan={columnCount}>
                      {t($ => $.pointsAudit.empty)}
                    </TableCell>
                  </TableRow>
                )}
                {events?.data.map((row) => (
                  <TableRow
                    data-testid={`points-audit-row-${row.id}`}
                    hover
                    key={row.id}
                    onClick={() => setSelectedEvent(row)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={NO_WRAP}>
                      {t($ => $.pointsAudit.dateTime, { date: row.createdAt })}
                    </TableCell>
                    {isAllRetailers && (
                      <TableCell>
                        {retailerNameById.get(row.retailerId) ?? row.retailerId}
                      </TableCell>
                    )}
                    <TableCell>{actorLabel(row)}</TableCell>
                    <TableCell>
                      <Chip label={eventTypeLabel(row.eventType, t)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Text category="body-medium">{describeAuditEvent(row, t)}</Text>
                      {renderDiff(row)}
                    </TableCell>
                    <TableCell>{row.reason ?? '—'}</TableCell>
                    <TableCell>{row.importFileName ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <VSpacer size="4" />
          {!!events && (
            <Stack alignItems="center" gap="8px">
              <Pagination
                count={Math.max(1, Math.ceil(events.total / PAGE_LIMIT))}
                onChange={(_, next) => setPage(next - 1)}
                page={page + 1}
              />
              <Text category="body-small">
                {t($ => $.pointsAudit.rowCount, { count: events.total })}
              </Text>
            </Stack>
          )}

      {!!selectedEvent && (
        <AuditEventDetailDialog
          event={selectedEvent}
          onClose={() => setSelectedEvent(undefined)}
        />
      )}

      <VSpacer size="10" />
    </Container>
  );
};
